import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { useFeatureFlags } from '@platform/ui-config';
import SelectSingle from '@radius/ui-library/dist/SelectSingle';
import type { SelectSingleOption } from '@radius/ui-library/dist/SelectSingle';
import {
	DEFAULT_LANGUAGE_TAG,
	PSEUDO_LANGUAGE_TAG,
	SUPPORTED_LANGUAGES,
} from '@radius/ui-utils';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { isI18nDebugActive } from '../Auxillary/i18nDebug';
import { I18N_LANGUAGE_STORAGE_KEY } from '../i18n';

const ENABLE_LANGUAGE_CONFIGURATION_FLAG = 'enableLanguageConfiguration';

const AVAILABLE_LANGUAGES: ReadonlyArray<string> = [
	DEFAULT_LANGUAGE_TAG,
	PSEUDO_LANGUAGE_TAG,
	'es-419',
	'pt-BR',
];

/**
 * Human-readable label for a BCP 47 tag in the currently active language
 * (e.g. `de` shows as "German" in English, "Deutsch" in German). Falls back
 * to the raw tag if `Intl.DisplayNames` can't resolve it.
 */
const getLanguageLabel = (tag: string, displayLanguage: string): string => {
	try {
		const displayNames = new Intl.DisplayNames([displayLanguage], {
			type: 'language',
		});

		return displayNames.of(tag) ?? tag;
	} catch {
		return tag;
	}
};

const LanguageSelector: FC = () => {
	const { t } = useTranslation();
	const flags = useFeatureFlags();
	const currentLanguage = i18n.resolvedLanguage || i18n.language;

	// The picker is only meaningful when language configuration is unlocked —
	// matches the gating in `initI18n` (LD flag on, or debug tooling active).
	const isLanguageConfigVisible =
		Boolean(flags?.[ENABLE_LANGUAGE_CONFIGURATION_FLAG]) ||
		isI18nDebugActive;

	const options = useMemo<Array<SelectSingleOption>>(
		() =>
			SUPPORTED_LANGUAGES.filter(
				(tag) =>
					AVAILABLE_LANGUAGES.includes(tag) &&
					// The pseudo locale is a debug/QA aid, never shown to end users.
					(tag !== PSEUDO_LANGUAGE_TAG || isI18nDebugActive),
			).map((tag) => ({
				value: tag,
				label: getLanguageLabel(tag, currentLanguage),
			})),
		[currentLanguage],
	);

	const selectedOption = useMemo(
		() => options.find((option) => option.value === currentLanguage),
		[options, currentLanguage],
	);

	const handleChange = useCallback(
		(option: SelectSingleOption | null): void => {
			if (!option) return;

			const nextLanguage = String(option.value);

			// Persist the explicit choice so it survives a page reload; this is
			// the stored preference `detectLanguage` reads on next init.
			localStorage.setItem(I18N_LANGUAGE_STORAGE_KEY, nextLanguage);
			i18n.changeLanguage(nextLanguage);
		},
		[],
	);

	if (!isLanguageConfigVisible) return null;

	return (
		<SelectSingle
			ariaLabel={t('header.label.language')}
			isSearchable
			onChange={handleChange}
			options={options}
			placeholder={t('header.text.select_language')}
			testId="header-language-selector"
			value={selectedOption}
			variant="translucent"
		/>
	);
};

export default LanguageSelector;
